// src/screens/PersonalInfoScreen.js
//
// The details screen, built from the activation's own configuration rather than
// from three hardcoded inputs.
//
// Which questions appear, in what order, and which are compulsory all come from
// `guestFields` on the activation, falling back to its campaign and then to the
// original first name / last name / email. An activation that has never been
// configured therefore behaves exactly as it always has, which matters because
// there are printed QR codes in the field pointing at this screen.
//
// Identity is established by the registerGuest Cloud Function. The previous
// approach created a Firebase Auth account whose password was the guest's own
// email plus a fixed suffix, so anyone who knew the pattern could sign in as any
// guest. Do not reintroduce it.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { registerGuest } from "../services/voucherService";
import { saveUserData } from "../services/localStorageService";
import {
  activeGuestFields,
  validateGuestForm,
  DIAL_CODES,
  maxDobFor,
  MIN_DOB,
} from "../config/guestFields";
import { toast } from "react-toastify";

const PersonalInfoScreen = ({ setUserData, guestFields, eventId }) => {
  const navigate = useNavigate();

  // Resolved once. `guestFields` is passed down from the activation document;
  // with nothing there this is the original three.
  const fields = useMemo(() => activeGuestFields(guestFields), [guestFields]);

  const [formData, setFormData] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.id, ""])),
  );
  const [dialCodes, setDialCodes] = useState(() =>
    Object.fromEntries(
      fields.filter((f) => f.dialCode).map((f) => [f.id, f.dialCode]),
    ),
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // Set when somebody is too young. Replaces the form entirely rather than
  // sitting under an input they could edit and resubmit.
  const [turnedAway, setTurnedAway] = useState(null);

  const setValue = (id, value) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // The dial code chosen per field travels with the value, so a guest who
    // switched to Namibia gets their number read as a Namibian one.
    const withDial = fields.map((f) =>
      f.dialCode ? { ...f, dialCode: dialCodes[f.id] || f.dialCode } : f,
    );

    const result = validateGuestForm(withDial, formData);

    if (result.blocked) {
      setTurnedAway(result.blocked);
      return;
    }
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setLoading(true);
    try {
      const registration = await registerGuest(result.clean, eventId);

      if (!registration || !registration.customerId) {
        toast.error("We could not save your details. Please check your connection.");
        setLoading(false);
        return;
      }

      // The server has the final say on the age check, and says so by refusing.
      if (registration.blocked) {
        setTurnedAway(registration.blocked);
        setLoading(false);
        return;
      }

      if (registration.isReturning) toast.success("Welcome back!");

      const userData = { uid: registration.customerId, ...result.clean };
      setUserData(userData);
      saveUserData(userData);
      navigateNext();
    } catch (error) {
      console.error("Error registering guest:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const navigateNext = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const qrDataParam = queryParams.get("data");
    const idParam = queryParams.get("id");

    if (qrDataParam) navigate("/promotion?" + queryParams.toString());
    else if (idParam) navigate("/promotion?id=" + idParam);
    else navigate("/scan");
  };

  /* --- turned away ------------------------------------------------------- */
  if (turnedAway) {
    return (
      <div className="screen-container">
        <h1 className="screen-title">Sorry</h1>
        <div className="pi-blocked">
          <p>{turnedAway}</p>
          <p className="pi-blocked__sub">
            Thanks for stopping by. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  /* --- the form ---------------------------------------------------------- */
  return (
    <div className="screen-container">
      <h1 className="screen-title">Your details</h1>

      <form onSubmit={handleSubmit} noValidate>
        {fields.map((f) => (
          <div className="form-group" key={f.id}>
            <label htmlFor={f.id}>
              {f.label}
              {!f.required && <span className="pi-optional">optional</span>}
            </label>

            {f.id === "mobile" ? (
              // A country picker beside the number. Defaulted to whatever the
              // activation was set up with, +27 unless somebody changed it.
              <div className="pi-tel">
                <select
                  className="pi-tel__code"
                  value={dialCodes[f.id] || f.dialCode}
                  onChange={(e) =>
                    setDialCodes((p) => ({ ...p, [f.id]: e.target.value }))
                  }
                  aria-label="Country code"
                >
                  {DIAL_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
                <input
                  id={f.id}
                  name={f.id}
                  // `tel` is what opens the phone keypad rather than the
                  // alphabetic keyboard. inputMode says the same thing again for
                  // the browsers that read that instead.
                  type="tel"
                  inputMode="tel"
                  autoComplete={f.autoComplete}
                  value={formData[f.id] || ""}
                  onChange={(e) => setValue(f.id, e.target.value)}
                  placeholder={f.placeholder}
                  maxLength={f.maxLength}
                />
              </div>
            ) : f.id === "dob" ? (
              // Native. On a phone this is the system's own wheel or calendar,
              // with no keyboard at all; on a desktop it can be typed straight
              // in. `max` stops the picker offering a date that would be turned
              // away, so the common case never reaches an error message.
              <input
                id={f.id}
                name={f.id}
                type="date"
                autoComplete={f.autoComplete}
                value={formData[f.id] || ""}
                onChange={(e) => setValue(f.id, e.target.value)}
                min={MIN_DOB}
                max={maxDobFor(f.minAge || 0)}
              />
            ) : (
              <input
                id={f.id}
                name={f.id}
                type={f.inputType}
                inputMode={f.inputMode}
                autoComplete={f.autoComplete}
                value={formData[f.id] || ""}
                onChange={(e) => setValue(f.id, e.target.value)}
                placeholder={f.placeholder}
                maxLength={f.maxLength}
              />
            )}

            {errors[f.id] && <div className="error-message">{errors[f.id]}</div>}
          </div>
        ))}

        <button type="submit" className="button" disabled={loading}>
          {loading ? "Submitting..." : "Continue"}
        </button>
      </form>
    </div>
  );
};

export default PersonalInfoScreen;
